import { ExportProductTier } from 'api/ExportHubAPI';

/**
 * Resolves the parent/child tree an uploaded product file describes, so the
 * Create Database wizard can preview it before anything is written and then
 * replay it as `addBomLink` calls once the products exist.
 *
 * Each row names its parent by `parent_sku` (matched against another row's
 * internal SKU, falling back to its name). Everything the server would reject
 * (spec §4.5 — tier rules, circular references) is caught here first, reported
 * as a row issue, and the offending link is dropped: the product still imports,
 * it just lands unlinked.
 */

// Deepest chain rendered/walked. Mirrors HierarchyView's MAX_DEPTH.
const MAX_PLAN_DEPTH = 12;

const PARENT_TIERS: ExportProductTier[] = ['Assembly', 'Sub-assembly'];
const CHILD_TIERS: ExportProductTier[] = ['Component', 'Sub-assembly'];

const key = (s: string): string => s.trim().toLowerCase();

export interface BomPlanRow {
  /** 1-based row number in the uploaded file, header excluded. */
  rowNumber: number;
  name: string;
  /** internal_sku, or '' when the column isn't mapped/filled. */
  sku: string;
  /** Raw parent_sku cell — the SKU or name of the assembly this belongs to. */
  parentRef: string;
  /** Tier read from the file, if the column was mapped and the value valid. */
  tier?: ExportProductTier;
  /** How many of this product go into its parent (a link attribute). */
  bomQuantity?: number;
}

export interface BomPlanIssue {
  rowNumber: number;
  name: string;
  message: string;
}

export interface BomPlanNode {
  /** Index into the plan's row array. */
  index: number;
  quantity?: number;
  children: BomPlanNode[];
}

export interface BomPlan {
  /** parentIndex[i] = row index of i's parent, or -1 for no (usable) link. */
  parentIndex: number[];
  /** Tier each row will be created with — from the file, inferred, or default. */
  tiers: ExportProductTier[];
  /** True where tiers[i] came from the structure rather than the file. */
  inferred: boolean[];
  /** Trees to be created — only rows that end up with children. */
  roots: BomPlanNode[];
  /** Rows that end up with neither a parent nor children. */
  standalone: number[];
  issues: BomPlanIssue[];
  /** Links that will actually be created. */
  linkCount: number;
}

/**
 * Tier when the file leaves it blank. A row with children and no parent is the
 * top of a tree (Assembly); with both, it's in the middle (Sub-assembly); a
 * leaf is a Component — which is also the server-side default, so a row with no
 * structure at all is left un-inferred.
 */
const inferTier = (
  hasChildren: boolean,
  hasParent: boolean,
): { tier: ExportProductTier; inferred: boolean } => {
  if (hasChildren && !hasParent) return { tier: 'Assembly', inferred: true };
  if (hasChildren) return { tier: 'Sub-assembly', inferred: true };
  if (hasParent) return { tier: 'Component', inferred: true };
  return { tier: 'Component', inferred: false };
};

export function buildBomPlan(rows: BomPlanRow[]): BomPlan {
  const n = rows.length;
  const issues: BomPlanIssue[] = [];
  const parentIndex: number[] = [];
  for (let i = 0; i < n; i++) parentIndex.push(-1);

  const flag = (i: number, message: string): void => {
    issues.push({ rowNumber: rows[i].rowNumber, name: rows[i].name, message: message });
  };

  // --- 1. Resolve each parent reference to a row -----------------------------
  // SKU first, then name. First row wins a duplicate, and the duplicate is
  // reported so the file can be corrected.
  const bySku = new Map<string, number>();
  const duplicateSkus = new Set<string>();
  rows.forEach((r, i) => {
    const k = key(r.sku);
    if (!k) return;
    if (bySku.has(k)) duplicateSkus.add(k);
    else bySku.set(k, i);
  });
  const byName = new Map<string, number>();
  rows.forEach((r, i) => {
    const k = key(r.name);
    if (k && !byName.has(k)) byName.set(k, i);
  });

  rows.forEach((r, i) => {
    const ref = r.parentRef.trim();
    if (!ref) return;
    const k = key(ref);
    const target = bySku.has(k) ? (bySku.get(k) as number) : byName.get(k);
    if (target === undefined) {
      flag(i, `Parent "${ref}" isn't in this file — the product imports unlinked.`);
      return;
    }
    if (target === i) {
      flag(i, 'Listed as its own parent — the link is skipped.');
      return;
    }
    if (duplicateSkus.has(k)) {
      flag(i, `More than one row uses SKU "${ref}" — linking to the first.`);
    }
    parentIndex[i] = target;
  });

  // --- 2. Break circular references -----------------------------------------
  // Every row has at most one parent, so walking up from each unvisited row
  // finds any loop; the edge that closes it is cut.
  const UNVISITED = 0;
  const ON_PATH = 1;
  const DONE = 2;
  const state: number[] = [];
  for (let i = 0; i < n; i++) state.push(UNVISITED);
  for (let start = 0; start < n; start++) {
    if (state[start] !== UNVISITED) continue;
    const path: number[] = [];
    let cur = start;
    while (cur !== -1 && state[cur] === UNVISITED) {
      state[cur] = ON_PATH;
      path.push(cur);
      cur = parentIndex[cur];
    }
    if (cur !== -1 && state[cur] === ON_PATH) {
      const last = path[path.length - 1];
      flag(
        last,
        `Its parent chain loops back to "${rows[cur].name}" — the link is skipped.`,
      );
      parentIndex[last] = -1;
    }
    path.forEach((i) => {
      state[i] = DONE;
    });
  }

  // --- 3. Tier per row -------------------------------------------------------
  const hasChildren: boolean[] = [];
  for (let i = 0; i < n; i++) hasChildren.push(false);
  parentIndex.forEach((p) => {
    if (p !== -1) hasChildren[p] = true;
  });

  const tiers: ExportProductTier[] = [];
  const inferred: boolean[] = [];
  rows.forEach((r, i) => {
    if (r.tier) {
      tiers.push(r.tier);
      inferred.push(false);
      return;
    }
    const guess = inferTier(hasChildren[i], parentIndex[i] !== -1);
    tiers.push(guess.tier);
    inferred.push(guess.inferred);
  });

  // --- 4. Enforce the tier rules the server enforces -------------------------
  // Only explicit tiers can conflict: an inferred Assembly never has a parent,
  // and an inferred Component never has children.
  parentIndex.forEach((p, i) => {
    if (p === -1) return;
    if (PARENT_TIERS.indexOf(tiers[p]) === -1) {
      flag(
        i,
        `Parent "${rows[p].name}" is a ${tiers[p]} — only an Assembly or Sub-assembly can have children.`,
      );
      parentIndex[i] = -1;
      return;
    }
    if (CHILD_TIERS.indexOf(tiers[i]) === -1) {
      flag(
        i,
        `Tier ${tiers[i]} can't be a child — set it to Sub-assembly or Component.`,
      );
      parentIndex[i] = -1;
    }
  });

  // --- 5. Assemble the trees -------------------------------------------------
  const childrenOf = new Map<number, number[]>();
  let linkCount = 0;
  parentIndex.forEach((p, i) => {
    if (p === -1) return;
    linkCount += 1;
    const arr = childrenOf.get(p);
    if (arr) arr.push(i);
    else childrenOf.set(p, [i]);
  });

  const build = (i: number, depth: number): BomPlanNode => ({
    index: i,
    quantity: rows[i].bomQuantity,
    children:
      depth >= MAX_PLAN_DEPTH
        ? []
        : (childrenOf.get(i) || []).map((c) => build(c, depth + 1)),
  });

  const roots: BomPlanNode[] = [];
  const standalone: number[] = [];
  rows.forEach((_r, i) => {
    if (parentIndex[i] !== -1) return;
    if (childrenOf.has(i)) roots.push(build(i, 0));
    else standalone.push(i);
  });

  return {
    parentIndex: parentIndex,
    tiers: tiers,
    inferred: inferred,
    roots: roots,
    standalone: standalone,
    issues: issues,
    linkCount: linkCount,
  };
}
